"use client";

/**
 * AppSwitcher — the menu-bar section that names the surface you are currently
 * in (Terminal / Box / Apps) and, on click, drops down to switch to another.
 *
 * This replaces the old header's separate Box/Apps nav links, in the same
 * spirit as an OS menu bar: the left of the bar always answers "where am I?".
 */

import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  BoxesIcon,
  ChevronDownIcon,
  LayoutGridIcon,
  MailIcon,
  type LucideIcon,
} from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@itoresearch/design-system";

import { useAppAuth } from "../../hooks/use-auth";

export interface AppSurface {
  id: string;
  label: string;
  description: string;
  to: string;
  icon: LucideIcon;
  /** Whether the current pathname belongs to this surface. */
  matches: (pathname: string) => boolean;
  /** Show only when signed in (Box and Apps require a session). */
  requiresAuth?: boolean;
}

/** Every top-level product surface reachable from the menu bar. */
export const APP_SURFACES: AppSurface[] = [
  {
    id: "terminal",
    label: "Terminal",
    description: "Research workspaces",
    to: "/",
    icon: LayoutGridIcon,
    matches: (p) => p === "/" || p.startsWith("/terminal"),
  },
  {
    id: "box",
    label: "Box",
    description: "Ito mailbox",
    to: "/box",
    icon: MailIcon,
    matches: (p) => p.startsWith("/box"),
    requiresAuth: true,
  },
  {
    id: "apps",
    label: "Apps",
    description: "Ito compute",
    to: "/apps",
    icon: BoxesIcon,
    matches: (p) => p.startsWith("/apps"),
    requiresAuth: true,
  },
];

/** The surface owning a pathname (falls back to Terminal). */
export function currentApp(pathname: string): AppSurface {
  return (
    APP_SURFACES.find((app) => app.matches(pathname)) ?? APP_SURFACES[0]!
  );
}

export function AppSwitcher() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { user } = useAppAuth();

  const active = currentApp(pathname);
  const ActiveIcon = active.icon;
  const available = APP_SURFACES.filter(
    (app) => !app.requiresAuth || Boolean(user),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Current app: ${active.label}. Switch app`}
            className="h-7 gap-1.5 px-2 font-medium"
          >
            <ActiveIcon className="size-3.5" />
            <span>{active.label}</span>
            <ChevronDownIcon className="size-3 opacity-60" />
          </Button>
        }
      />
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>Switch app</DropdownMenuLabel>
        {available.map((app) => {
          const Icon = app.icon;
          return (
            <DropdownMenuItem
              key={app.id}
              onClick={() => {
                if (!app.matches(pathname)) void navigate({ to: app.to });
              }}
            >
              <Icon />
              <span className="flex-1 truncate">{app.label}</span>
              <span className="text-xs text-muted-foreground">
                {app.description}
              </span>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void navigate({ to: "/design" })}>
          <LayoutGridIcon />
          <span className="flex-1 truncate">Design system</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
